import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TrackCard from './TrackCard';
import ProjectStack from '../projects/ProjectStack';
import SearchFilter from './SearchFilter';
import { fetchTracks, fetchProjects } from '../../services/api';
import { Howl } from 'howler';
import './TrackGallery.css';

const TrackGallery = () => {
    const [tracks, setTracks] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('');
    const [currentTrack, setCurrentTrack] = useState(null);
    const [sound, setSound] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showProjects, setShowProjects] = useState(true);

    useEffect(() => {
        const getData = async () => {
            try {
                const [tracksData, projectsData] = await Promise.all([
                    fetchTracks(),
                    fetchProjects()
                ]);
                setTracks(tracksData);
                setProjects(projectsData);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        getData();
    }, []);

    const playTrack = (track) => {
        // Stop current sound if playing
        if (sound) {
            sound.stop();
        }

        // For demo purposes, we'll simulate playing
        setCurrentTrack(track);
        setIsPlaying(true);
        
        // Stop after 30 seconds for demo
        setTimeout(() => {
            setIsPlaying(false);
            setCurrentTrack(null);
        }, 30000);
    };

    const filteredTracks = tracks.filter(track => {
        const matchesSearch = track.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            track.artist.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesGenre = !selectedGenre || track.genre === selectedGenre;
        return matchesSearch && matchesGenre;
    });

    const genres = [...new Set(tracks.map(track => track.genre))];

    if (loading) {
        return (
            <div className="track-gallery loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading tracks...</p>
                </div>
            </div>
        );
    }

    return (
        <motion.div 
            className="track-gallery"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <div className="gallery-header">
                <motion.h2 
                    className="gallery-title"
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    TRACK GALLERY
                </motion.h2>
                
                <SearchFilter
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedGenre={selectedGenre}
                    setSelectedGenre={setSelectedGenre}
                    genres={genres}
                />
                
                <div className="view-toggles">
                    <button 
                        className={`toggle-btn ${showProjects ? 'active' : ''}`}
                        onClick={() => setShowProjects(true)}
                    >
                        PROJECTS
                    </button>
                    <button 
                        className={`toggle-btn ${!showProjects ? 'active' : ''}`}
                        onClick={() => setShowProjects(false)}
                    >
                        TRACKS ONLY
                    </button>
                </div>
            </div>

            <div className="gallery-content">
                <AnimatePresence mode="wait">
                    {showProjects && (
                        <motion.div 
                            key="projects"
                            className="projects-section"
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 50 }}
                            transition={{ duration: 0.5 }}
                        >
                            <h3 className="section-title">PROJECTS</h3>
                            <div className="projects-grid">
                                <ProjectStack projects={projects} />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div 
                    className="tracks-section"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: showProjects ? 0.3 : 0 }}
                >
                    <h3 className="section-title">
                        {showProjects ? 'INDIVIDUAL TRACKS' : 'ALL TRACKS'}
                    </h3>
                    <div className="tracks-grid">
                        {filteredTracks.map((track, index) => (
                            <motion.div
                                key={track.id}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ 
                                    duration: 0.5, 
                                    delay: index * 0.1,
                                    type: "spring",
                                    stiffness: 100
                                }}
                            >
                                <TrackCard
                                    track={track}
                                    onClick={playTrack}
                                    isPlaying={currentTrack?.id === track.id && isPlaying}
                                />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default TrackGallery;